import React, { useRef } from 'react';
import { ActionMenu, ActionMenuItem } from '../action-menu';
import { IconID } from '../icon';
import { Horizontal } from '../layout';
import { Popover } from '../popover';
import { Button, ButtonProps } from './button';
import styles from './multi-select-button.module.scss';

export interface MultiSelectButtonProps {
  /**
   * Action menu config for all the buttons
   * added as part of dropdown.
   */
  actionsMenuConfig: ActionMenuItem[];
  /**
   * To disable the dropdown option
   */
  isDropdownDisabled: boolean;
  /**
   * Button props for the primary button
   */
  primaryButton: ButtonProps;
  /**
   * className for the button
   */
  className?: string;
}

export const MultiSelectButton: React.FC<MultiSelectButtonProps> = ({
  actionsMenuConfig,
  isDropdownDisabled,
  primaryButton,
  className,
}: MultiSelectButtonProps) => {
  const [isDropdownVisible, setDropdownVisible] = React.useState(false);
  const toggleDropDown = () => {
    setDropdownVisible((dropdownVisible) => !dropdownVisible);
  };

  const chevronRef = useRef<HTMLDivElement>(null);

  const wrapWithCloseDropdown = (onClick: any) => {
    return (event: any) => {
      setDropdownVisible(false);
      onClick(event);
    };
  };

  const getActionMenu = () => {
    const updatedActionMenuConfig = actionsMenuConfig.map((ele: ActionMenuItem) => {
      return {
        ...ele,
        onClick: wrapWithCloseDropdown(ele.onClick),
      };
    });
    return (
      <ActionMenu
        show={isDropdownVisible}
        menuItemGroups={[{ title: '', items: updatedActionMenuConfig }]}
      />
    );
  };

  return (
    <Horizontal className={className}>
      <Button
        type={primaryButton.type}
        icon={primaryButton.icon}
        text={primaryButton.text}
        isDisabled={primaryButton.isDisabled}
        tooltip={primaryButton.tooltip}
        className={styles.primaryButton}
        data-testid="multi-select-button-primary"
        onClick={wrapWithCloseDropdown(primaryButton.onClick)}
        borderPlacement="leftOnly"
      />
      <div ref={chevronRef}>
        <Button
          type={primaryButton.type}
          icon={IconID.CHEVRON_DOWN}
          isDisabled={isDropdownDisabled}
          className={styles.dropdownButton}
          onClick={toggleDropDown}
          borderPlacement="rightOnly"
          data-testid="multi-select-button-dropdown"
        />
      </div>
      <Popover
        targetRef={chevronRef}
        content={getActionMenu()}
        align="bottom-end"
        isVisible={isDropdownVisible}
        data-testid="multi-select-button-action-menu"
        onClickOutside={() => setDropdownVisible(false)}
      />
    </Horizontal>
  );
};
